<template>
  <custom-collapse-item
    ref="collapseItem"
    auto
    class="my-3 collapse-item section bg-gray"
    expanded-icon="fas fa-chevron-circle-right color-gray-7"
    minimized-icon="fas fa-chevron-circle-down color-warning"
    parent-id="factors-collapse"
    read-only-icon="fas fa-plus-circle color-gray-2"
    start-expanded
    title="GED"
    @expandButtonClick="$emit('expandButtonClick')"
  >
    <div class="bg-white p-3 rounded">
      <div class="bg-light p-3 rounded mb-3 border">
        <div class="bg-white ged-legend-box">
          <div class="row font-weight-bold mb-2">
            <div class="col-3">Nivel</div>
            <div class="col-9">Grado</div>
          </div>

          <div
            v-for="level in levels"
            :key="`ged-level-${level.id}`"
            class="row align-items-center mb-3"
          >
            <div class="col-3">{{ level.name }}</div>
            <div class="col-9">
              <span
                v-if="getGradeByLevel(level.id)"
                class="ged-chip"
                :style="getGradeStyle(getGradeByLevel(level.id))"
              >
                {{ getGradeByLevel(level.id).name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-for="item in items"
        :key="`ged-item-${item.areaId}`"
        class="ged-item-card mb-3"
      >
        <div class="ged-item-title">
          {{ item.name }}
        </div>

        <div class="row align-items-end p-3">
          <div class="col-md-3">
            <label>Nivel Requerido de la Profesión:</label>

            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors: levelErrors }"
              :name="`${item.name} - Nivel Requerido`"
              rules="required"
            >
              <custom-drop-down
                clearable
                :disabled="isReadOnly"
                fieldkey="id"
                fieldname="name"
                :options="levels"
                placeholder="Seleccione"
                :value="item.requiredLevel"
                @input="updateItem(item, 'requiredLevel', $event)"
              />

              <small class="text-danger">
                {{ levelErrors[0] }}
              </small>
            </validation-provider>
            <div v-else class="text-muted">
              {{ item.requiredLevel.name | empty }}
            </div>
          </div>

          <div class="col-md-3">
            <label>Perfil del Evaluado:</label>

            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors: evaluatedErrors }"
              :name="`${item.name} - Perfil Evaluado`"
              rules="required"
            >
              <custom-drop-down
                clearable
                :disabled="isReadOnly"
                fieldkey="id"
                fieldname="name"
                :options="levels"
                placeholder="Seleccione"
                :value="item.evaluatedLevel"
                @input="updateItem(item, 'evaluatedLevel', $event)"
              />

              <small class="text-danger">
                {{ evaluatedErrors[0] }}
              </small>
            </validation-provider>
            <div v-else class="text-muted">
              {{ item.evaluatedLevel.name | empty }}
            </div>
          </div>

          <div class="col-md-3 d-flex flex-column align-items-center">
            <label>Grado:</label>
            <div>
              <span
                v-if="getGradeByLevel(item.evaluatedLevel)"
                class="ged-chip"
                :style="getGradeStyle(getGradeByLevel(item.evaluatedLevel))"
              >
                {{ getGradeByLevel(item.evaluatedLevel).name }}
              </span>
            </div>
          </div>

          <div
            class="col-12 col-lg-3 d-flex justify-content-lg-end ged-answer-wrapper mt-3 mt-lg-0"
          >
            <validation-provider
              v-slot="{ errors: answerErrors }"
              class="w-100"
              :name="`${item.name} - Respuesta`"
              rules="required"
            >
              <input v-model="item.selectedAnswer" type="hidden" />

              <div class="d-flex flex-wrap justify-content-lg-end">
                <button
                  v-for="answer in answers"
                  :key="`ged-answer-${item.areaId}-${answer.id}`"
                  class="ged-answer-btn"
                  :class="{
                    active:
                      item.selectedAnswer &&
                      item.selectedAnswer.id === answer.id,
                  }"
                  :disabled="isReadOnly"
                  type="button"
                  @click="item.selectedAnswer = answer"
                >
                  <i class="far fa-check-circle d-block mb-1" />
                  {{ answer.name }}
                </button>
              </div>

              <small class="text-danger d-block mt-1">
                {{ answerErrors[0] }}
              </small>
            </validation-provider>
          </div>
        </div>
      </div>

      <div>
        <label>Observaciones:</label>
        <custom-text-area
          v-model="model.comment"
          :count-classes="['d-block', 'small-counter', 'text-right']"
          :disabled="isReadOnly"
          :maxlength="5000"
          placeholder="Comentario"
          show-char-count
        />
      </div>
    </div>
  </custom-collapse-item>
</template>

<script>
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';

export default {
  name: 'GedOccupationalFactors',
  components: {
    CustomCollapseItem,
    CustomDropDown,
    CustomTextArea,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    levels() {
      return this.model.levels || [];
    },
    grades() {
      return this.model.grades || [];
    },
    answers() {
      return this.model.answers || [];
    },
    items() {
      return this.model.items || [];
    },
  },
  methods: {
    setExpanded(expand) {
      this.$refs.collapseItem.isExpended = expand;
    },
    getGradeByLevel(level) {
      if (!level) return null;

      const levelId = typeof level === 'object' ? level.id : level;
      const levelIndex = this.levels.findIndex((x) => x.id === levelId);

      if (levelIndex === -1) return null;

      return this.grades[levelIndex] || null;
    },
    getGradeStyle(grade) {
      if (!grade) return {};

      return {
        backgroundColor: `${grade.color}25`,
        border: `1px solid ${grade.color}`,
        color: grade.color,
      };
    },
    updateItem(item, field, value) {
      const model = { ...this.model };
      const items = [...(model.items || [])];

      const index = items.findIndex((x) => x.areaId === item.areaId);
      if (index === -1) return;

      items[index] = {
        ...items[index],
        [field]: value,
      };

      model.items = items;
      this.model = model;
    },
  },
};
</script>

<style scoped>
.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.collapse-item ::v-deep .color-warning {
  color: #ff5b5c !important;
}

::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
/* -------------- */
.ged-legend-box {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem;
}

.ged-chip {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.ged-item-card {
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  background: #fafafa;
}

.ged-item-title {
  padding: 0.9rem 1rem;
  font-weight: 600;
  border-bottom: 1px solid #e6e6e6;
}

.ged-answer-wrapper {
  border-left: 1px solid #e1e1e1;
  padding-left: 1rem;
}

@media (max-width: 991.98px) {
  .ged-answer-wrapper {
    border-left: none;
    border-top: 1px solid #e1e1e1;
    padding-left: 0;
    padding-top: 1rem;
    justify-content: flex-start !important;
    flex-wrap: wrap;
  }

  .ged-answer-btn {
    margin-left: 0;
    margin-right: 0.45rem;
    margin-bottom: 0.45rem;
  }
}

.ged-answer-btn {
  min-width: 95px;
  border: 1px solid #f2f2f2;
  background: #f7f7f7;
  color: #a8b7b5;
  padding: 0.65rem 0.75rem;
  margin-left: 0.45rem;
  border-radius: 6px 6px 0 0;
  font-size: 12px;
}

.ged-answer-btn i {
  font-size: 16px;
  color: #9bb0ad;
}

.ged-answer-btn.active {
  background: #2db782;
  border-color: #2db782;
  color: #fff;
}

.ged-answer-btn.active i {
  color: #fff;
}

.ged-answer-btn:disabled.active {
  background: #3f5b60;
  border-color: #3f5b60;
  color: #fff;
}

.ged-answer-btn:disabled:not(.active) {
  background: #f4f4f4;
  border-color: #f4f4f4;
  color: #aeb8b6;
  opacity: 1;
}

.ged-answer-btn:disabled:not(.active) i {
  color: #9bb0ad;
}

.ged-answer-btn:disabled {
  cursor: not-allowed;
}
</style>
